import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderIcon from "@mui/icons-material/Folder";
import { Chip } from "@mui/material";

import { ContextDisplayNames } from "@/Chat/ChatSidebar/hooks/useRobotoLLMContext";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { RobotoLLMContext } from "@/shared/domain/chat/ChatRecord";

import styles from "./RobotoLLMContextWidget.module.css";

export type ContextType = "dataset" | "file" | "viz_state";

interface RobotoLLMContextWidgetProps {
  context: RobotoLLMContext;
  displayNames: ContextDisplayNames;
  onDelete: (contextType: ContextType, contextContent: string) => void;
}

interface RobotoLLMContextChipProps {
  contextType: ContextType;
  contextContent: string;
  tooltip?: string;
  onDelete: () => void;
}

const MAX_FILE_PATH_LENGTH = 20;

/**
 * Truncates a file path in the middle if it exceeds the max length.
 * e.g., "path/to/some/long/file.bag" -> "path/to...file.bag"
 */
const truncateMiddle = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) {
    return str;
  }
  const ellipsis = "...";
  const charsToShow = maxLength - ellipsis.length;
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);
  return str.slice(0, frontChars) + ellipsis + str.slice(-backChars);
};

export const RobotoLLMContextChip = ({
  contextType,
  contextContent,
  tooltip,
  onDelete,
}: RobotoLLMContextChipProps) => {
  let icon;
  if (contextType === "dataset") {
    icon = <FolderIcon className={styles.chipIconDataset} />;
  } else if (contextType === "file") {
    icon = <DescriptionIcon className={styles.chipIconFile} />;
  } else if (contextType === "viz_state") {
    icon = <AutoGraphIcon className={styles.chipIconFile} />;
  }

  const chip = (
    <Chip
      icon={icon}
      label={contextContent}
      size="small"
      onDelete={onDelete}
      className={styles.chip}
      classes={{
        label: styles.chipLabel,
      }}
      sx={{
        "& .MuiChip-icon": {
          marginRight: "1px",
        },
      }}
    />
  );

  if (tooltip) {
    return <RobotoTooltip title={tooltip}>{chip}</RobotoTooltip>;
  }

  return chip;
};

/**
 * Widget that displays the current RobotoLLMContext with dataset and file names.
 * Falls back to IDs when names are not available.
 * Inspired by AugmentCode's PyCharm plugin context bar.
 */
export const RobotoLLMContextWidget = ({
  context,
  displayNames,
  onDelete,
}: RobotoLLMContextWidgetProps) => {
  // Don't render if there's no context
  const hasContext =
    context.dataset_ids.length > 0 || context.file_ids.length > 0;

  if (!hasContext) {
    return null;
  }

  // The visualizer state is passed as opaque, but we can assume it's a visualizer v1 state object and look for
  // panel information as a means to give a visual cue in the context bar
  const numPanels =
    context.visualizer_state &&
    typeof context.visualizer_state === "object" &&
    "panels" in context.visualizer_state &&
    typeof context.visualizer_state.panels === "object" &&
    context.visualizer_state.panels !== null
      ? Object.keys(context.visualizer_state.panels).length
      : 0;
  const pluralizeSuffix = numPanels === 1 ? "" : "s";

  return (
    <div className={styles.contextContainer}>
      {context.dataset_ids.map((datasetId) => {
        const datasetName = displayNames.datasets[datasetId];
        return (
          <RobotoLLMContextChip
            key={datasetId}
            contextType="dataset"
            contextContent={datasetId}
            tooltip={datasetName}
            onDelete={() => onDelete("dataset", datasetId)}
          />
        );
      })}

      {context.file_ids.map((fileId) => {
        const filePath = displayNames.files[fileId] ?? fileId;
        const needsTruncation = filePath.length > MAX_FILE_PATH_LENGTH;
        return (
          <RobotoLLMContextChip
            key={fileId}
            contextType="file"
            contextContent={truncateMiddle(filePath, MAX_FILE_PATH_LENGTH)}
            tooltip={needsTruncation ? filePath : undefined}
            onDelete={() => onDelete("file", fileId)}
          />
        );
      })}

      {context.visualizer_state && (
        <RobotoLLMContextChip
          contextType="viz_state"
          contextContent={
            numPanels === 0
              ? "empty_visualizer"
              : `${numPanels}_viz_panel${pluralizeSuffix}`
          }
          onDelete={() => onDelete("viz_state", "")}
        />
      )}
    </div>
  );
};
